import React, { useEffect, useMemo, useState } from 'react';

import { Link, Modal, Text, TreeSelectComponent } from 'v3/components';
import { usePolicyRegister } from '../../context';
import DOMPurify from 'dompurify';
import Space from 'v3/components/Space';
import { fetchNcmProducts } from 'utils/fetches';
import { StyledModalButton } from 'v3/pages/Policy/styles';
import { validateProduct } from '../../utils';
import { cooperplaceApi } from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import * as S from './styles';

export function ProductsModal() {
  const {
    productsModal,
    setOperations,
    currentOpIndex,
    setCurrentOpIndex,
    currentPdIndex,
    setCurrentPdIndex,
    operations,
    editAction,
    setEditAction,
    setErrors,
    policy
  } = usePolicyRegister();

  const [newProduct, setNewProduct] = useState([]);
  const [productsOptions, setProductsOptions] = useState([]);
  const [fetchingNcm, setFetchingNcm] = useState(false);
  const [error, setError] = useState({});
  const [policyInUseError, setPolicyInUseError] = useState(null);

  const snackbar = useSnackbar();

  const cleanText = (val) =>
    DOMPurify.sanitize(val ?? '', { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }).trim();

  function handleAdd() {
    const products = operations[currentOpIndex].products?.map((p) => p.products);
    const newProductsCode = newProduct?.map((p) => p?.code);
    const isValid = validateProduct(products, newProductsCode);
    if (!isValid) {
      setError({
        product: 'Algum produto informado já está contemplado na operação',
      });
      return;
    }
    setOperations((oldOperations) =>
      oldOperations.map((op, index) => {
        if (index === currentOpIndex) {
          return {
            ...op,
            products: [
              ...op?.products,
              {
                products: [...newProduct],
                vehicles: [],
                responsibility_security: [],
                routes: [],
              },
            ],
          };
        }
        return op;
      }),
    );
    setNewProduct([]);
    setCurrentOpIndex(null);
    setCurrentPdIndex(null);
    setError({});
    setErrors({});
    setEditAction(null);
    productsModal.close();
  }

  async function handleEdit() {
    const products = operations[currentOpIndex].products
      ?.filter((_, index) => index !== currentPdIndex)
      .map(p => p?.products);
    const newProductsCode = newProduct?.map(p => p?.code);
    const isValid = validateProduct(products, newProductsCode);
    if (!isValid) {
      setError({
        product: 'Algum produto informado já está contemplado na operação',
      });
      return;
    }

    try {
      await cooperplaceApi.post(
        'policy-validator/product/validate',
        {
          action: 'update',
          policyId: policy?.id,
          policyProductId: operations[currentOpIndex].products[currentPdIndex].id,
          productNcmIds: newProduct?.map(p => p.id),
        }
      );

      let newState = [...operations];
      newState[currentOpIndex].products[currentPdIndex].products = [
        ...newProduct,
      ];
      setOperations(newState);
  
      setNewProduct([]);
      setCurrentOpIndex(null);
      setCurrentPdIndex(null);
      setEditAction(null)
      setError({});
      productsModal.close();
    } catch(err) {
      if (err.response?.data?.type === 'IN_USE_ERROR') {
        setPolicyInUseError({
          error: err?.response?.data?.error,
          loads: err.response?.data?.loads || [],
        });
        return
      }

      snackbar.show(
        <Text style={{ whiteSpace: 'pre' }}>{err?.response?.data?.error || 'Erro ao validar produto!' }</Text>,
        {
          type: 'error',
          duration: 20000,
          showClose: true,
        }
      );
    }

  }

  function handleDuplicate() {
    const currentProduct = operations[currentOpIndex].products[currentPdIndex]
    if (currentProduct.products.find(p => newProduct.find(np => np.code === p.code))) {
      setError({
        product: 'Informe um produto diferente do grupo atual',
      });
      return;
    }

    const products = operations[currentOpIndex].products
      ?.filter((_, index) => index !== currentPdIndex)
      .map((p) => p?.products);
    const newProductsCode = newProduct?.map((p) => p?.code);
    const isValid = validateProduct(products, newProductsCode);
    if (!isValid) {
      setError({
        product: 'Algum produto informado já está contemplado na operação',
      });
      return;
    }

    let newState = [...operations];

    const duplicateProduct = JSON.parse(JSON.stringify(currentProduct))
    delete duplicateProduct.id;
    duplicateProduct.products = [...newProduct]
    for (const vehicle of duplicateProduct.vehicles) {
      delete vehicle.id
      for (const range of vehicle.ranges) {
        delete range.id
        for (const equipment of range.monitoringEquipaments) {
          delete equipment.id;
        }
      }
    }
    
    newState[currentOpIndex].products = [
      ...newState[currentOpIndex].products,
      duplicateProduct
    ]

    setOperations(newState);
    setNewProduct([]);
    setCurrentOpIndex(null);
    setCurrentPdIndex(null);
    setError({});
    setEditAction(null);
    productsModal.close();
  }

  const convertToTreeData = (node) => ({
    value: node.id,
    code: node?.code,
    title: `${node?.code} - ${node?.name}`,
    name: node?.name,
    chapterName: node?.chapterName,
    chapterCode: node?.chapterCode,
    children: node.children ? node.children.map(convertToTreeData) : [],
  });

  async function handleSearch(value) {
    try {
      setFetchingNcm(true);
      const response = await fetchNcmProducts(value);
      const processedResponse = response.map(convertToTreeData);
      setProductsOptions(processedResponse);
    } catch (err) {
    } finally {
      setFetchingNcm(false);
    }
  }

  const filterTreeNode = (input, treeNode) => {
    return treeNode;
  };

  function handleCloseModal() {
    setNewProduct([]);
    setCurrentOpIndex(null);
    setCurrentPdIndex(null);
    setError({});
    setErrors({});
    setPolicyInUseError(null);
    productsModal.close();
  }

  useEffect(() => {
    if (currentOpIndex !== null && currentPdIndex !== null && productsModal.isOpen) {
      setNewProduct(operations[currentOpIndex].products[currentPdIndex]?.products);
    }
  }, [productsModal, currentOpIndex, currentPdIndex, operations]);

  function onClick() {
    if (editAction === 'duplicate') {
      handleDuplicate()
    } else {
      currentOpIndex !== null && currentPdIndex !== null ? handleEdit() : handleAdd()
    }
  }

  const label = useMemo(() => {
    if (editAction == 'duplicate') {
      return 'Duplicar produto'
    }

    if (currentOpIndex !== null && currentPdIndex !== null) {
      return 'Editar produto'
    }

    return 'Novo produto'
  }, [editAction, currentOpIndex, currentPdIndex])

  return (
    <Modal
      size="lg"
      show={productsModal.isOpen}
      handleClose={handleCloseModal}
      heading={
        <Text type="body" color="dark" weight="400">
          {label}
        </Text>
      }
      body={
        <div>
          <TreeSelectComponent
            label="Digite o código ou nome ncm"
            labelInValue
            showSearch
            dropdownStyle={{
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid #e5eaee',
            }}
            onSearch={(inputValue) => handleSearch(cleanText(inputValue))}
            filterTreeNode={filterTreeNode}
            treeData={productsOptions}
            placeholder="Digite para buscar"
            allowClear
            loading={fetchingNcm}
            multiple
            getPopupContainer={(trigger) => trigger?.parentElement || document.body}
            onBlur={() => setProductsOptions([])}
            value={newProduct?.map((item) => {
              return item?.id && item?.chapterCode
                ? {
                    label: (
                      <span>
                        <i>
                          {item.chapterCode}
                          {' - '}
                          {item.chapterName}{' '}
                        </i>
                        <br />
                        <span>
                          {item.code}
                          {' - '}
                          {item.name}
                        </span>
                      </span>
                    ),
                    value: item?.id,
                  }
                : item?.chapter
                ? {
                    label: (
                      <span>
                        <i>
                          {item?.chapter?.code}
                          {' - '}
                          {item?.chapter?.name}{' '}
                        </i>
                        <br />
                        <span>
                          {item?.code}
                          {' - '}
                          {item?.name}
                        </span>
                      </span>
                    ),
                    value: item?.id,
                  }
                : item?.id
                ? {
                    label: (
                      <span>
                        <span>
                          {item?.code} {' - '}
                          {item?.name}
                        </span>
                      </span>
                    ),
                    value: item?.id,
                  }
                : null;
            })}
            onChange={(value) => {
              if (value?.length === 0 || !value) {
                setNewProduct([]);
              } else {
                const valuesIds = value?.map((item) => item.value);
                const filtered = newProduct.filter((item) => valuesIds.includes(item.id));
                setNewProduct(filtered);
              }
            }}
            onSelect={(value, node) => {
              setNewProduct((old) => [
                ...old,
                {
                  label: `${node?.code} - ${node?.name}`,
                  id: node?.value,
                  value: node?.value,
                  name: node?.name,
                  code: node?.code,
                  chapterCode: node?.chapterCode,
                  chapterName: node?.chapterName,
                },
              ]);
            }}
            error={error?.product}
          />
          {policyInUseError && (
            <S.ModalError>
              <p className='mb-1'>{policyInUseError?.error}</p>
              {policyInUseError?.loads?.map((load) => (
                <div key={load}>carga #{load}: <Link to={`/cargas/${load}`}>acessar carga</Link></div>
              ))}
            </S.ModalError>
          )}
        </div>
      }
      footer={
        <Space x="0.5rem">
          <StyledModalButton variant="secondary" size="sm" onClick={handleCloseModal}>
            <Text>Cancelar</Text>
          </StyledModalButton>
          <StyledModalButton
            size="sm"
            onClick={onClick}
            disabled={newProduct?.length === 0}
          >
            <Text>
              {currentOpIndex !== null && currentPdIndex !== null
                ? 'Salvar'
                : 'Adicionar'}
            </Text>
          </StyledModalButton>
        </Space>
      }
    />
  );
}
